import { AlertProps } from '../components/ui/Alert';
import { testId } from '../utils';
import {
  NAME_MAX,
  PHONE_CHARACTER_MAX,
  PHONE_DIGIT_MIN,
} from '../utils/constants';
import { useCountries, useRegions } from './locationsHooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { Cornerstone } from '@rpi/api';
import { useTranslation } from '@rpi/i18n';
import type { AxiosError } from 'axios';
import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import validator, { PostalCodeLocale } from 'validator';
import { z } from 'zod';

export function useGetAddressAlertMessage(): (
  mutationError: ReturnType<
    typeof Cornerstone.useAddAddress | typeof Cornerstone.useUpdateAddress
  >['error'],
) => string | null {
  const { t } = useTranslation(undefined, { keyPrefix: 'route' });

  return React.useCallback(
    (mutationError) => {
      if (!mutationError) {
        return null;
      }

      if (
        (mutationError as AxiosError<any>).response?.data?.errorCode ===
        'AVALARA_VALIDATION_FAILED'
      ) {
        return t('user.addresses.invalid_address');
      }

      return t('auth.generic_error');
    },
    [t],
  );
}

export function useAddressAlertProps(
  mutation: ReturnType<
    typeof Cornerstone.useAddAddress | typeof Cornerstone.useUpdateAddress
  >,
): AlertProps | null {
  const getAlertMessage = useGetAddressAlertMessage();

  const alertMessage = React.useMemo(
    () => getAlertMessage?.(mutation.error),
    [getAlertMessage, mutation.error],
  );

  if (!alertMessage) {
    return null;
  }

  return {
    ...testId('alert'),
    variant: 'error',
    children: alertMessage,
  };
}

export const useAddressForm = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'route.user' });
  const { countries, loading: countriesLoading } = useCountries();
  const navigate = useNavigate();
  const AddressSchema = useMemo(() => {
    return z
      .object({
        firstName: z
          .string()
          .min(1, t('addresses.input.required')!)
          .max(
            NAME_MAX,
            t('settings.account.input.first_name.max', {
              count: NAME_MAX,
            })!,
          ),
        lastName: z
          .string()
          .min(1, t('addresses.input.required')!)
          .max(
            NAME_MAX,
            t('settings.account.input.last_name.max', {
              count: NAME_MAX,
            })!,
          ),
        company: z.string().optional(),
        phone: z
          .string()
          .max(
            PHONE_CHARACTER_MAX,
            t('addresses.input.phone.max', { count: PHONE_CHARACTER_MAX })!,
          )
          .min(
            PHONE_DIGIT_MIN,
            t('addresses.input.phone.min', { count: PHONE_DIGIT_MIN })!,
          )
          .refine(
            (val) => !/[^\s-+0-9]/g.test(val),
            t('addresses.input.phone.invalid')!,
          ),
        countryIso: z.string().min(1, t('addresses.input.required')!),
        addressLine1: z.string().min(1, t('addresses.input.required')!),
        addressLine2: z.string().optional(),
        city: z.string().min(1, t('addresses.input.required')!),
        regionIsoCode: z.string().min(1, t('addresses.input.required')!),
        postalCode: z.string().min(1, t('addresses.input.required')!),
        defaultShipping: z.boolean(),
        poBoxApo: z.boolean(),
      })
      .refine(
        (data) =>
          data.countryIso
            ? validator.isPostalCode(
                data.postalCode,
                data.countryIso as PostalCodeLocale,
              )
            : false,
        {
          message: t('addresses.input.zip.invalid')!,
          path: ['postalCode'],
        },
      );
  }, [t]);

  type AddressSchema = z.infer<typeof AddressSchema>;

  const form = useForm<AddressSchema>({
    resolver: zodResolver(AddressSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      company: '',
      phone: '',
      countryIso: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      regionIsoCode: '',
      postalCode: '',
      defaultShipping: false,
      poBoxApo: false,
    },
  });

  const regionQuery = useRegions(form.watch('countryIso'));

  const onCancel = React.useCallback(() => {
    navigate('..', {
      relative: 'route',
    });
  }, [navigate]);

  const { regions } = regionQuery;
  const { watch, resetField } = form;
  const countryIso = watch('countryIso');
  const regionIsoCode = watch('regionIsoCode');

  React.useEffect(() => {
    if (regions.length < 1) {
      return;
    }

    const isRegionPresent = regions.find(
      (region) =>
        region.value === regionIsoCode || region.label === regionIsoCode,
    );

    if (!isRegionPresent) {
      resetField('regionIsoCode');
    }
  }, [countryIso, regionIsoCode, regions, resetField]);

  return {
    form,
    regionQuery,
    countries,
    countriesLoading,
    AddressSchema,
    onCancel,
  };
};
